import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../../components/AuthContext';
import { FaDownload, FaPrint, FaUser, FaRupeeSign, FaFileAlt, FaChartPie, FaList, FaThLarge, FaInfoCircle, FaEye, FaCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getCurrentMonthDates, getFiscalQuarterMeta, getFiscalQuarterOptions } from '../../../utils/dateUtils';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

const QuotationsReports = () => {
  const { user } = useContext(AuthContext);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [dateFilter, setDateFilter] = useState(getCurrentMonthDates());
  const [salesPersonFilter, setSalesPersonFilter] = useState('');
  const [salesPersons, setSalesPersons] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [summary, setSummary] = useState({
    totalQuotations: 0,
    totalAmount: 0,
    employeeContribution: [],
    statusContribution: []
  });
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [quotationDetails, setQuotationDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [viewMode, setViewMode] = useState('row');
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const [quarterOptions, setQuarterOptions] = useState([]);
  const [quarterStart, setQuarterStart] = useState('');
  const [quarterEnd, setQuarterEnd] = useState('');

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const syncDarkMode = () => {
      setIsDarkMode(localStorage.getItem('darkMode') === 'true');
    };

    window.addEventListener('storage', syncDarkMode);
    window.addEventListener('focus', syncDarkMode);

    return () => {
      window.removeEventListener('storage', syncDarkMode);
      window.removeEventListener('focus', syncDarkMode);
    };
  }, []);

  useEffect(() => {
    const options = getFiscalQuarterOptions(16);
    setQuarterOptions(options);
    const currentQuarter = getFiscalQuarterMeta();
    setQuarterStart(currentQuarter.id);
    setQuarterEnd(currentQuarter.id);

    fetchQuotations(currentQuarter.id, currentQuarter.id);
    fetchSalesPersons();
  }, []);

  const fetchSalesPersons = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (user?.user?.company?._id) {
        params.append('companyId', user.user.company._id);
      } else if (user?.user?.company) {
        params.append('companyId', user.user.company);
      }
      
      const response = await axios.get(`${API_BASE}/reports/quotations/employees?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSalesPersons(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching sales persons:', error);
    }
  };

  const fetchQuotations = async (overrideQuarterStart, overrideQuarterEnd) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      const activeQuarterStart = overrideQuarterStart || quarterStart;
      const activeQuarterEnd = overrideQuarterEnd || quarterEnd;
      
      if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);
      if (salesPersonFilter) params.append('salesPerson', salesPersonFilter);
      if (activeQuarterStart && activeQuarterEnd) {
        params.append('quarterStart', activeQuarterStart);
        params.append('quarterEnd', activeQuarterEnd);
      }
      
      // Company filtering
      if (user?.user?.company?._id) {
        params.append('companyId', user.user.company._id);
      } else if (user?.user?.company) {
        params.append('companyId', user.user.company);
      }

      const response = await axios.get(`${API_BASE}/reports/quotations?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setQuotations(response.data.quotations || []);
      setTotalAmount(response.data.summary?.totalAmount || 0);
      setSummary(response.data.summary || {
        totalQuotations: 0,
        totalAmount: 0,
        employeeContribution: [],
        statusContribution: []
      });
    } catch (error) {
      console.error('Error fetching quotations:', error);
      toast.error('Failed to fetch quotation reports');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    if ((quarterStart && !quarterEnd) || (!quarterStart && quarterEnd)) {
      toast.info('Please select both quarter start and quarter end for quarter-wise comparison.');
      return;
    }

    fetchQuotations();
  };

  const handlePrintQuotation = (quotation) => {
    // Generate proper PDF print URL based on quotation type
    let printUrl = '';
    
    if (quotation.quotationType === 'QuotationNew') {
      // For QuotationNew, use the rlugroup.hbs template
      const apiEndpoint = `/quotation-new/data/${quotation._id}`;
      printUrl = `https://rlugroup.in/pdf/convert?fileName=rlugroup.hbs&url=${process.env.REACT_APP_API_BASE_URL}${apiEndpoint}`;
    } else if (quotation.quotationType === 'TallyQuotation') {
      // For TallyQuotation, use the rlugroup-tally-quotation.hbs template with full print type
      printUrl = `https://rlugroup.in/pdf/convert?fileName=rlugroup-tally-quotation.hbs&url=${process.env.REACT_APP_API_BASE_URL}/tally-quotation/print/${quotation._id}?printType=full`;
    } else {
      // Fallback for other quotation types or legacy quotations
      printUrl = `https://rlugroup.in/pdf/convert?fileName=rlugroup.hbs&url=${process.env.REACT_APP_API_BASE_URL}/quotation-new/data/${quotation._id}`;
    }
    
    // Open the PDF in a new tab
    window.open(printUrl, '_blank');
  };

  const isCompletable = (quotation) => {
    const status = (quotation?.status || '').toString().toUpperCase();
    return status === 'PENDING' || status === '';
  };

  const handleCompleteQuotation = (quotation) => {
    if (!isCompletable(quotation)) {
      toast.info('Only pending quotations can be completed');
      return;
    }

    Swal.fire({
      title: 'Complete Quotation',
      html: `
        <div class="text-left">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Date of Completion:</label>
            <input type="date" id="completionDate" class="w-full p-2 border border-gray-300 rounded-md" required />
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Completion Notes:</label>
            <textarea id="completionNotes" class="w-full p-2 border border-gray-300 rounded-md h-24" placeholder="Enter completion notes"></textarea>
          </div>
        </div>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Complete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#10b981',
      preConfirm: () => {
        const completionDate = document.getElementById('completionDate').value;
        const completionNotes = document.getElementById('completionNotes').value;
        if (!completionDate) {
          Swal.showValidationMessage('Please enter completion date');
          return false;
        }
        return { completionDate, completionNotes };
      },
    }).then(async (result) => {
      if (!result.isConfirmed || !result.value) return;

      try {
        const token = localStorage.getItem('token');
        const { completionDate, completionNotes } = result.value;
        const response = await axios.post(
          `${API_BASE}/reports/quotations/${quotation._id}/complete`,
          {
            type: quotation.quotationType,
            completionDate,
            completionNotes
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          toast.success('Quotation marked as completed');
          // Close details modal if open for this quotation
          if (selectedQuotation?._id === quotation._id) {
            setShowQuotationModal(false);
            setSelectedQuotation(null);
            setQuotationDetails(null);
          }
          fetchQuotations();
        } else {
          toast.error(response.data?.message || 'Failed to complete quotation');
        }
      } catch (error) {
        console.error('Error completing quotation:', error);
        const message = error.response?.data?.message || 'Failed to complete quotation';
        toast.error(message);
      }
    });
  };

  const handleViewQuotationDetails = async (quotation) => {
    setLoadingDetails(true);
    setSelectedQuotation(quotation);
    setShowQuotationModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE}/reports/quotations/details/${quotation._id}?type=${quotation.quotationType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setQuotationDetails(response.data);
    } catch (error) {
      console.error('Error fetching quotation details:', error);
      toast.error('Failed to fetch quotation details');
      setQuotationDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const downloadExportBlob = (blobData, filename) => {
    const url = window.URL.createObjectURL(new Blob([blobData]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleExportDateRangeExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const companyId = user?.user?.company;
      
      console.log('Export - User context:', user);
      console.log('Export - Company ID:', companyId);
      
      // Create descriptive filename with date range and filters
      const startDateStr = dateFilter.startDate ? new Date(dateFilter.startDate).toISOString().split('T')[0] : 'all';
      const endDateStr = dateFilter.endDate ? new Date(dateFilter.endDate).toISOString().split('T')[0] : 'all';
      const salesPersonName = salesPersons.find(sp => sp._id === salesPersonFilter)?.name || 'all-salespeople';
      const timestamp = new Date().toISOString().split('T')[0];

      const filename = `quotations-date-range_${startDateStr}-to-${endDateStr}_${salesPersonName}_${timestamp}.xlsx`;

      const exportData = {
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
        salesPerson: salesPersonFilter,
        companyId: companyId,
        exportType: 'excel'
      };
      
      console.log('Export - Sending data:', exportData);

      const response = await axios.post(`${API_BASE}/reports/quotations/export`, exportData, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      downloadExportBlob(response.data, filename);
      toast.success('Date-range Excel export completed successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export date-range excel');
    }
  };

  const handleExportQuarterExcel = async () => {
    try {
      if (!quarterStart || !quarterEnd) {
        toast.info('Please select quarter start and quarter end to export quarter-wise report.');
        return;
      }

      const token = localStorage.getItem('token');
      const companyId = user?.user?.company;
      const salesPersonName = salesPersons.find(sp => sp._id === salesPersonFilter)?.name || 'all-salespeople';
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `quotations-quarterwise-executive_${quarterStart}_to_${quarterEnd}_${salesPersonName}_${timestamp}.xlsx`;

      const exportData = {
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
        salesPerson: salesPersonFilter,
        companyId,
        quarterStart,
        quarterEnd,
        exportType: 'excel'
      };

      const response = await axios.post(`${API_BASE}/reports/quotations/export`, exportData, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      downloadExportBlob(response.data, filename);
      toast.success('Quarter-wise executive Excel export completed successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export quarter-wise excel');
    }
  };

    const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Helper function to format salesmen names
  const formatSalesmenNames = (salesmen) => {
    if (!salesmen || salesmen.length === 0) {
      return 'N/A';
    }
    
    // If salesmen is an array of objects with name property
    if (Array.isArray(salesmen)) {
      const names = salesmen.map(salesman => 
        typeof salesman === 'object' ? (salesman.name || salesman.firstName || 'Unknown') : salesman
      ).filter(name => name && name !== 'Unknown');
      
      if (names.length === 0) return 'N/A';
      if (names.length === 1) return names[0];
      if (names.length === 2) return names.join(' & ');
      return `${names[0]} & ${names.length - 1} others`;
    }
    
    // If salesmen is a single object
    if (typeof salesmen === 'object') {
      return salesmen.name || salesmen.firstName || 'N/A';
    }
    
    return salesmen || 'N/A';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const getRecipientName = (quotation) => {
    return quotation?.recipient?.name || quotation?.recipient?.firmName || quotation?.recipient?.businessName || 'N/A';
  };

  const getStatusLabel = (status) => {
    const normalized = (status || 'PENDING').toString().toUpperCase();
    switch (normalized) {
      case 'PENDING':
        return 'Pending Follow-up';
      case 'COMPLETED':
        return 'Completed';
      case 'CANCELLED':
        return 'Cancelled';
      case 'PARTIAL-COMPLETE':
        return 'Partially Completed';
      case 'REFFOLLOW':
      case 'NEXT-FOLLOW-UP':
        return 'Follow-up Scheduled';
      default:
        return normalized;
    }
  };

  const getQuotationAgeInDays = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diff = now.getTime() - created.getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  };

  const getPriorityLevel = (quotation) => {
    const normalizedStatus = (quotation?.status || 'PENDING').toString().toUpperCase();
    const age = getQuotationAgeInDays(quotation?.createdAt);

    if (normalizedStatus === 'PENDING' && age >= 7) return 'High';
    if (normalizedStatus === 'PENDING' && age >= 3) return 'Medium';
    if (normalizedStatus === 'REFFOLLOW' || normalizedStatus === 'NEXT-FOLLOW-UP') return 'Scheduled';
    if (normalizedStatus === 'PARTIAL-COMPLETE') return 'In Progress';
    if (normalizedStatus === 'COMPLETED') return 'Closed';
    if (normalizedStatus === 'CANCELLED') return 'Closed';
    return 'Normal';
  };

  const getPriorityTextColor = (priority) => {
    if (isDarkMode) {
      if (priority === 'High') return 'text-rose-300';
      if (priority === 'Medium') return 'text-amber-300';
      if (priority === 'Scheduled') return 'text-sky-300';
      if (priority === 'In Progress') return 'text-orange-300';
      if (priority === 'Closed') return 'text-emerald-300';
      return 'text-slate-300';
    }

    if (priority === 'High') return 'text-rose-700';
    if (priority === 'Medium') return 'text-amber-700';
    if (priority === 'Scheduled') return 'text-sky-700';
    if (priority === 'In Progress') return 'text-orange-700';
    if (priority === 'Closed') return 'text-emerald-700';
    return 'text-gray-700';
  };

  const getShareOfTotal = (amount) => {
    if (!totalAmount || totalAmount <= 0) return '0.0';
    return (((amount || 0) / totalAmount) * 100).toFixed(1);
  };

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658'];

  // Prepare chart data for react-chartjs-2
  const employeeChartData = {
    labels: summary.employeeContribution?.map(emp => emp.name) || [],
    datasets: [
      {
        data: summary.employeeContribution?.map(emp => emp.totalAmount) || [],
        backgroundColor: COLORS,
        borderColor: COLORS.map(color => color + '80'),
        borderWidth: 1,
      }
    ]
  };

  const statusChartData = {
    labels: summary.statusContribution?.map(status => status.status) || [],
    datasets: [
      {
        data: summary.statusContribution?.map(status => status.totalAmount) || [],
        backgroundColor: COLORS,
        borderColor: COLORS.map(color => color + '80'),
        borderWidth: 1,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          color: isDarkMode ? '#cbd5e1' : '#4b5563',
        }
      },
      tooltip: {
        backgroundColor: isDarkMode ? '#0f172a' : '#111827',
        titleColor: '#f8fafc',
        bodyColor: '#e2e8f0',
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = formatCurrency(context.parsed);
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  const getStatusColor = (status) => {
    if (isDarkMode) {
      switch (status?.toLowerCase()) {
        case 'pending': return 'bg-yellow-900/40 text-yellow-300 border border-yellow-700';
        case 'approved':
        case 'completed': return 'bg-green-900/40 text-green-300 border border-green-700';
        case 'rejected': return 'bg-red-900/40 text-red-300 border border-red-700';
        case 'cancelled': return 'bg-gray-800 text-gray-200 border border-gray-600';
        case 'partial-complete': return 'bg-orange-900/40 text-orange-300 border border-orange-700';
        case 'reffollow':
        case 'next-follow-up': return 'bg-sky-900/40 text-sky-300 border border-sky-700';
        default: return 'bg-blue-900/40 text-blue-300 border border-blue-700';
      }
    }

    switch (status?.toLowerCase()) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const pageContainerClass = isDarkMode
    ? 'p-6 min-h-screen bg-slate-950 text-slate-100'
    : 'p-6';

  const cardClass = isDarkMode
    ? 'bg-slate-900 border border-slate-700 rounded-lg shadow-md p-6'
    : 'bg-white rounded-lg shadow-md p-6';

  const tableWrapClass = isDarkMode
    ? 'bg-slate-900 border border-slate-700 rounded-lg shadow-md overflow-hidden'
    : 'bg-white rounded-lg shadow-md overflow-hidden';

  const inputClass = isDarkMode
    ? 'w-full p-2 border border-slate-600 bg-slate-800 text-slate-100 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
    : 'w-full p-2 border border-gray-300 rounded-md';

  const selectedSalesPersonName = salesPersons.find((sp) => sp._id === salesPersonFilter)?.name || 'All Sales Persons';
  const startDateLabel = dateFilter.startDate ? formatDate(dateFilter.startDate) : 'Any date';
  const endDateLabel = dateFilter.endDate ? formatDate(dateFilter.endDate) : 'Any date';
  const reportWindowLabel = `${startDateLabel} - ${endDateLabel}`;
  const completedCount = quotations.filter((q) => (q?.status || '').toString().toUpperCase() === 'COMPLETED').length;
  const cancelledCount = quotations.filter((q) => (q?.status || '').toString().toUpperCase() === 'CANCELLED').length;
  const pendingCount = quotations.filter((q) => (q?.status || '').toString().toUpperCase() === 'PENDING').length;
  const followUpCount = quotations.filter((q) => {
    const status = (q?.status || '').toString().toUpperCase();
    return status === 'REFFOLLOW' || status === 'NEXT-FOLLOW-UP';
  }).length;
  const partialCount = quotations.filter((q) => (q?.status || '').toString().toUpperCase() === 'PARTIAL-COMPLETE').length;
  const completionRate = quotations.length > 0 ? ((completedCount / quotations.length) * 100).toFixed(1) : '0.0';
  const dominantStatus = summary.statusContribution?.[0]?.status ? getStatusLabel(summary.statusContribution[0].status) : 'N/A';
  const topPerformer = summary.employeeContribution?.[0] || null;
  const pendingValue = quotations
    .filter((q) => (q?.status || '').toString().toUpperCase() === 'PENDING')
    .reduce((acc, q) => acc + (q?.totalAmount || 0), 0);
  const activePipelineValue = quotations
    .filter((q) => {
      const status = (q?.status || '').toString().toUpperCase();
      return status === 'PENDING' || status === 'REFFOLLOW' || status === 'NEXT-FOLLOW-UP' || status === 'PARTIAL-COMPLETE';
    })
    .reduce((acc, q) => acc + (q?.totalAmount || 0), 0);
  const agedPendingCount = quotations
    .filter((q) => (q?.status || '').toString().toUpperCase() === 'PENDING' && getQuotationAgeInDays(q?.createdAt) >= 7)
    .length;
  const stalePendingValue = quotations
    .filter((q) => (q?.status || '').toString().toUpperCase() === 'PENDING' && getQuotationAgeInDays(q?.createdAt) >= 7)
    .reduce((acc, q) => acc + (q?.totalAmount || 0), 0);
  const highestValueQuotation = quotations.reduce((max, q) => {
    if (!max) return q;
    return (q?.totalAmount || 0) > (max?.totalAmount || 0) ? q : max;
  }, null);
  const avgQuotationAge = quotations.length > 0
    ? (quotations.reduce((acc, q) => acc + getQuotationAgeInDays(q?.createdAt), 0) / quotations.length).toFixed(1)
    : '0.0';
  const avgPendingAge = pendingCount > 0
    ? (
      quotations
        .filter((q) => (q?.status || '').toString().toUpperCase() === 'PENDING')
        .reduce((acc, q) => acc + getQuotationAgeInDays(q?.createdAt), 0) / pendingCount
    ).toFixed(1)
    : '0.0';
  const topPerformerShare = topPerformer && totalAmount > 0
    ? ((topPerformer.totalAmount / totalAmount) * 100).toFixed(1)
    : '0.0';
  const topTwoContribution = summary.employeeContribution?.slice(0, 2)
    .reduce((acc, emp) => acc + (emp?.totalAmount || 0), 0) || 0;
  const topTwoShare = totalAmount > 0 ? ((topTwoContribution / totalAmount) * 100).toFixed(1) : '0.0';
  const activePipelineCount = pendingCount + followUpCount + partialCount;
  const quarterComparison = summary.quarterComparison || [];
  const executiveInsights = summary.executiveInsights || [];
  const latestQuarter = quarterComparison.length > 0 ? quarterComparison[quarterComparison.length - 1] : null;

  const formatDelta = (value, suffix = '') => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return 'N/A';
    const num = Number(value);
    const sign = num > 0 ? '+' : '';
    return `${sign}${num.toFixed(1)}${suffix}`;
  };

  const recommendations = [];
  if (agedPendingCount > 0) recommendations.push(`${agedPendingCount} quotations are pending for 7+ days; prioritize these first.`);
  if (Number(completionRate) < 30 && quotations.length > 0) recommendations.push(`Completion rate is ${completionRate}%; review reminder closure discipline and follow-up quality.`);
  if (pendingValue > totalAmount * 0.5 && totalAmount > 0) recommendations.push(`Over 50% of value is still pending (${formatCurrency(pendingValue)}); accelerate conversions.`);
  if (Number(topTwoShare) > 65) recommendations.push(`Top two contributors drive ${topTwoShare}% of value; diversify pipeline ownership.`);
  if (followUpCount === 0 && pendingCount > 0) recommendations.push('No follow-up-scheduled quotations are present despite pending workload; verify reminder scheduling.');
  if (recommendations.length === 0) recommendations.push('Pipeline indicators look balanced for current filters; continue monitoring aged pending and closure quality.');

  return (
    <div className={pageContainerClass}>
      <div className="mb-6">
        <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`}>Quotations Reports</h2>
        <p className={isDarkMode ? 'text-slate-300' : 'text-gray-600'}>Descriptive performance report for quotation pipeline, value contribution, and follow-up health.</p>
      </div>

      <div className={`${cardClass} mb-6`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold mb-1 flex items-center">
              <FaInfoCircle className="mr-2 text-blue-500" />
              Report Context
            </h3>
            <p className={isDarkMode ? 'text-slate-300 text-sm' : 'text-gray-600 text-sm'}>
              Covering {quotations.length} quotations worth {formatCurrency(totalAmount)} for {selectedSalesPersonName} between {reportWindowLabel}.
            </p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <span className={`px-2 py-1 rounded-full ${isDarkMode ? 'bg-slate-800 text-slate-200 border border-slate-700' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>
                Pending: {pendingCount}
              </span>
              <span className={`px-2 py-1 rounded-full ${isDarkMode ? 'bg-sky-900/30 text-sky-300 border border-sky-700' : 'bg-sky-100 text-sky-700 border border-sky-200'}`}>
                Follow-up: {followUpCount}
              </span>
              <span className={`px-2 py-1 rounded-full ${isDarkMode ? 'bg-orange-900/30 text-orange-300 border border-orange-700' : 'bg-orange-100 text-orange-700 border border-orange-200'}`}>
                Partial: {partialCount}
              </span>
              <span className={`px-2 py-1 rounded-full ${isDarkMode ? 'bg-rose-900/30 text-rose-300 border border-rose-700' : 'bg-rose-100 text-rose-700 border border-rose-200'}`}>
                Aged pending (7+ days): {agedPendingCount}
              </span>
              <span className={`px-2 py-1 rounded-full ${isDarkMode ? 'bg-emerald-900/30 text-emerald-300 border border-emerald-700' : 'bg-emerald-100 text-emerald-700 border border-emerald-200'}`}>
                Cancelled: {cancelledCount}
              </span>
            </div>
          </div>
          <div className="text-sm">
            <p className={isDarkMode ? 'text-slate-300' : 'text-gray-600'}>
              Dominant status: <span className="font-semibold">{dominantStatus}</span>
            </p>
            <p className={isDarkMode ? 'text-slate-300' : 'text-gray-600'}>
              Completion rate: <span className="font-semibold">{completionRate}%</span>
            </p>
            <p className={isDarkMode ? 'text-slate-300' : 'text-gray-600'}>
              Pending value at risk: <span className="font-semibold">{formatCurrency(pendingValue)}</span>
            </p>
            <p className={isDarkMode ? 'text-slate-300' : 'text-gray-600'}>
              Active pipeline value: <span className="font-semibold">{formatCurrency(activePipelineValue)}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className={cardClass}>
          <p className={`text-xs uppercase tracking-wider mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Top Contributor</p>
          {topPerformer ? (
            <>
              <p className="text-lg font-semibold">{topPerformer.name}</p>
              <p className={isDarkMode ? 'text-slate-300 text-sm' : 'text-gray-600 text-sm'}>
                {topPerformer.count} quotations contributing {formatCurrency(topPerformer.totalAmount)} ({topPerformerShare}% share).
              </p>
            </>
          ) : (
            <p className={isDarkMode ? 'text-slate-400 text-sm' : 'text-gray-500 text-sm'}>No contributor data in this filter window.</p>
          )}
        </div>
        <div className={cardClass}>
          <p className={`text-xs uppercase tracking-wider mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Largest Opportunity</p>
          {highestValueQuotation ? (
            <>
              <p className="text-lg font-semibold">{formatCurrency(highestValueQuotation.totalAmount)}</p>
              <p className={isDarkMode ? 'text-slate-300 text-sm' : 'text-gray-600 text-sm'}>
                {highestValueQuotation.quotationId || highestValueQuotation.id || 'N/A'} for {getRecipientName(highestValueQuotation)}.
              </p>
            </>
          ) : (
            <p className={isDarkMode ? 'text-slate-400 text-sm' : 'text-gray-500 text-sm'}>No quotation data available.</p>
          )}
        </div>
        <div className={cardClass}>
          <p className={`text-xs uppercase tracking-wider mb-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Follow-up Alert</p>
          <p className="text-lg font-semibold">{agedPendingCount} aged pending</p>
          <p className={isDarkMode ? 'text-slate-300 text-sm' : 'text-gray-600 text-sm'}>
            {formatCurrency(stalePendingValue)} is tied in quotations pending for 7+ days.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <div className={cardClass}>
          <h3 className="text-lg font-semibold mb-3">Pipeline Health Insights</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Active pipeline</p>
              <p className="font-semibold">{activePipelineCount} quotations</p>
            </div>
            <div>
              <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Average quotation age</p>
              <p className="font-semibold">{avgQuotationAge} days</p>
            </div>
            <div>
              <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Average pending age</p>
              <p className="font-semibold">{avgPendingAge} days</p>
            </div>
            <div>
              <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Top-2 concentration</p>
              <p className="font-semibold">{topTwoShare}%</p>
            </div>
          </div>
        </div>

        <div className={cardClass}>
          <h3 className="text-lg font-semibold mb-3">Recommended Actions</h3>
          <div className="space-y-2 text-sm">
            {recommendations.map((tip, idx) => (
              <div
                key={idx}
                className={`p-3 rounded-md border ${isDarkMode ? 'border-slate-700 bg-slate-800 text-slate-200' : 'border-gray-200 bg-gray-50 text-gray-700'}`}
              >
                {tip}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <div className={cardClass}>
          <h3 className="text-lg font-semibold mb-3">Quarter-Wise Comparison</h3>
          {quarterComparison.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={isDarkMode ? 'text-slate-300 border-b border-slate-700' : 'text-gray-600 border-b border-gray-200'}>
                    <th className="py-2 pr-3 text-left">Quarter</th>
                    <th className="py-2 pr-3 text-right">Count</th>
                    <th className="py-2 pr-3 text-right">Value</th>
                    <th className="py-2 pr-3 text-right">Completion %</th>
                    <th className="py-2 text-right">QoQ Value %</th>
                  </tr>
                </thead>
                <tbody>
                  {quarterComparison.map((row) => (
                    <tr key={row.quarterId} className={isDarkMode ? 'border-b border-slate-800' : 'border-b border-gray-100'}>
                      <td className="py-2 pr-3">{row.quarterLabel}</td>
                      <td className="py-2 pr-3 text-right">{row.quotationCount}</td>
                      <td className="py-2 pr-3 text-right">{formatCurrency(row.totalAmount)}</td>
                      <td className="py-2 pr-3 text-right">{Number(row.completionRate || 0).toFixed(1)}%</td>
                      <td className={`py-2 text-right ${Number(row.deltaAmountPct || 0) >= 0 ? (isDarkMode ? 'text-emerald-300' : 'text-emerald-700') : (isDarkMode ? 'text-rose-300' : 'text-rose-700')}`}>
                        {row.deltaAmountPct === null ? 'N/A' : formatDelta(row.deltaAmountPct, '%')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={isDarkMode ? 'text-slate-400 text-sm' : 'text-gray-500 text-sm'}>No quarter-comparison data available for selected filters.</p>
          )}
        </div>

        <div className={cardClass}>
          <h3 className="text-lg font-semibold mb-3">Executive Quarter Snapshot</h3>
          {latestQuarter ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Latest Quarter</span>
                <span className="font-semibold">{latestQuarter.quarterLabel}</span>
              </div>
              <div className="flex justify-between">
                <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Quarter Value</span>
                <span className="font-semibold">{formatCurrency(latestQuarter.totalAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>QoQ Value Delta</span>
                <span className="font-semibold">{latestQuarter.deltaAmount === null ? 'N/A' : formatCurrency(latestQuarter.deltaAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>QoQ Count Delta</span>
                <span className="font-semibold">{latestQuarter.deltaCount === null ? 'N/A' : formatDelta(latestQuarter.deltaCount)}</span>
              </div>
              <div className="flex justify-between">
                <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>QoQ Completion Delta</span>
                <span className="font-semibold">{latestQuarter.deltaCompletionRate === null ? 'N/A' : formatDelta(latestQuarter.deltaCompletionRate, '%')}</span>
              </div>
            </div>
          ) : (
            <p className={isDarkMode ? 'text-slate-400 text-sm' : 'text-gray-500 text-sm'}>No latest quarter data available.</p>
          )}
        </div>
      </div>

      <div className={`${cardClass} mb-6`}>
        <h3 className="text-lg font-semibold mb-3">Executive Narrative</h3>
        <div className="space-y-2 text-sm">
          {(executiveInsights.length > 0 ? executiveInsights : ['No narrative insights available for this range.']).map((insight, idx) => (
            <div key={idx} className={`p-3 rounded-md border ${isDarkMode ? 'border-slate-700 bg-slate-800 text-slate-200' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
              {insight}
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className={`${cardClass} mb-6`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold">Filters</h3>
          <div className={`inline-flex rounded-md overflow-hidden border ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
            <button
              onClick={() => setViewMode('row')}
              className={`px-3 py-2 text-sm flex items-center ${viewMode === 'row'
                ? 'bg-blue-600 text-white'
                : isDarkMode
                  ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              title="Row View"
            >
              <FaList className="mr-2" /> Row
            </button>
            <button
              onClick={() => setViewMode('card')}
              className={`px-3 py-2 text-sm flex items-center ${viewMode === 'card'
                ? 'bg-blue-600 text-white'
                : isDarkMode
                  ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              title="Card View"
            >
              <FaThLarge className="mr-2" /> Card
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Quarter Start</label>
            <select
              value={quarterStart}
              onChange={(e) => setQuarterStart(e.target.value)}
              className={inputClass}
            >
              <option value="">Select quarter</option>
              {quarterOptions.map((quarter) => (
                <option key={quarter.id} value={quarter.id}>{quarter.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Quarter End</label>
            <select
              value={quarterEnd}
              onChange={(e) => setQuarterEnd(e.target.value)}
              className={inputClass}
            >
              <option value="">Select quarter</option>
              {quarterOptions.map((quarter) => (
                <option key={quarter.id} value={quarter.id}>{quarter.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Start Date</label>
            <input
              type="date"
              value={dateFilter.startDate}
              onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})}
              className={inputClass}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>End Date</label>
            <input
              type="date"
              value={dateFilter.endDate}
              onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})}
              className={inputClass}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Sales Person</label>
            <select
              value={salesPersonFilter}
              onChange={(e) => setSalesPersonFilter(e.target.value)}
              className={inputClass}
            >
              <option value="">All Sales Persons</option>
              {salesPersons.map(person => (
                <option key={person._id} value={person._id}>
                  {person.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end space-x-2 md:col-span-2">
            <button
              onClick={handleFilter}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <FaEye className="mr-2" />
              Filter
            </button>
            <button
              onClick={handleExportDateRangeExcel}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center relative group"
              title="Export selected date-range quotations Excel report"
            >
              <FaDownload className="mr-2" />
              Export Date Excel
              <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none ${isDarkMode ? 'bg-slate-700 text-slate-100' : 'bg-gray-800 text-white'}`}>
                Exports quotations list for selected date range and salesperson without quarter override
              </div>
            </button>
            <button
              onClick={handleExportQuarterExcel}
              className="px-4 py-2 bg-emerald-700 text-white rounded-md hover:bg-emerald-800 flex items-center relative group"
              title="Export quarter-wise executive Excel report"
            >
              <FaDownload className="mr-2" />
              Export Quarter Excel
              <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none ${isDarkMode ? 'bg-slate-700 text-slate-100' : 'bg-gray-800 text-white'}`}>
                Multi-sheet executive export: compact summary, quarter comparison, and detailed rows
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Employee-wise Contribution */}
        <div className={cardClass}>
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FaChartPie className="mr-2 text-blue-500" />
            Employee-wise Contribution
          </h3>
          {summary.employeeContribution?.length > 0 ? (
            <div>
              <div className="h-64 mb-4">
                <Pie data={employeeChartData} options={chartOptions} />
              </div>
              <div className="space-y-2">
                {summary.employeeContribution.map((employee, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      <span>{employee.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{formatCurrency(employee.totalAmount)}</div>
                      <div className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>{employee.count} quotations</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={`h-64 flex items-center justify-center ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              No data available
            </div>
          )}
        </div>

        {/* Status-wise Contribution */}
        <div className={cardClass}>
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FaChartPie className="mr-2 text-green-500" />
            Status-wise Contribution
          </h3>
          {summary.statusContribution?.length > 0 ? (
            <div>
              <div className="h-64 mb-4">
                <Pie data={statusChartData} options={chartOptions} />
              </div>
              <div className="space-y-2">
                {summary.statusContribution.map((status, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      <span className="capitalize">{getStatusLabel(status.status)}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{formatCurrency(status.totalAmount)}</div>
                      <div className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>{status.count} quotations</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={`h-64 flex items-center justify-center ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
        <div className={cardClass}>
          <div className="flex items-center">
            <FaFileAlt className="text-blue-500 text-2xl mr-3" />
            <div>
              <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Total Quotations in Scope</p>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`}>{quotations.length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Filtered by selected period and salesperson</p>
            </div>
          </div>
        </div>
        <div className={cardClass}>
          <div className="flex items-center">
            <FaRupeeSign className="text-green-500 text-2xl mr-3" />
            <div>
              <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Total Quotation Value</p>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`}>{formatCurrency(totalAmount)}</p>
              <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Combined value of all filtered quotations</p>
            </div>
          </div>
        </div>
        <div className={cardClass}>
          <div className="flex items-center">
            <FaUser className="text-purple-500 text-2xl mr-3" />
            <div>
              <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Average Ticket Size</p>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`}>
                {formatCurrency(quotations.length > 0 ? totalAmount / quotations.length : 0)}
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Mean quotation value in this report view</p>
            </div>
          </div>
        </div>
        <div className={cardClass}>
          <div className="flex items-center">
            <FaCheck className="text-emerald-500 text-2xl mr-3" />
            <div>
              <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Completion Performance</p>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`}>
                {completedCount} / {quotations.length}
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{completionRate}% of quotations completed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className={tableWrapClass}>
        <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          <h3 className="text-lg font-semibold">Quotations</h3>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            Detailed listing with current lifecycle status, assigned sales owner, and next action options.
          </p>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className={`mt-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Loading quotations...</p>
          </div>
        ) : (
          viewMode === 'row' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={isDarkMode ? 'bg-slate-800 divide-slate-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>
                    Date
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>
                    Type
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>
                    Quotation No
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>
                    Sales Person
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>
                    Customer Name
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>
                    Amount
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>
                    Aging / Priority
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`${isDarkMode ? 'bg-slate-900 divide-y divide-slate-700' : 'bg-white divide-y divide-gray-200'}`}>
                {quotations.length > 0 ? (
                  quotations.map((quotation, index) => (
                    <tr key={quotation._id || index} className={isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50'}>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                        {formatDate(quotation.createdAt)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {quotation.quotationType || 'Quotation'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {quotation.quotationId || quotation.id || 'N/A'}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                        {formatSalesmenNames(quotation.salesmen)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                        {getRecipientName(quotation)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                        <div>{formatCurrency(quotation.totalAmount)}</div>
                        <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{getShareOfTotal(quotation.totalAmount)}% of filtered value</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(quotation.status)}`}>
                          {getStatusLabel(quotation.status)}
                        </span>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                        <div>{getQuotationAgeInDays(quotation.createdAt)} days</div>
                        <div className={`text-xs font-medium ${getPriorityTextColor(getPriorityLevel(quotation))}`}>
                          {getPriorityLevel(quotation)} priority
                        </div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                        <button
                          onClick={() => handlePrintQuotation(quotation)}
                          className="text-green-600 hover:text-green-900 mr-3"
                          title="Print Quotation"
                        >
                          <FaPrint />
                        </button>
                        {isCompletable(quotation) && (
                          <button
                            onClick={() => handleCompleteQuotation(quotation)}
                            className="text-emerald-600 hover:text-emerald-900 mr-3"
                            title="Mark as Completed"
                          >
                            <FaCheck />
                          </button>
                        )}
                        <button
                          onClick={() => handleViewQuotationDetails(quotation)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Reminders"
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className={`px-6 py-8 text-center ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      No quotations found for the selected criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          ) : (
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {quotations.length > 0 ? quotations.map((quotation, index) => (
                <div
                  key={quotation._id || index}
                  className={`rounded-lg border p-4 ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white'}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-blue-500 font-semibold">{quotation.quotationType || 'Quotation'}</p>
                      <p className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                        {quotation.quotationId || quotation.id || 'N/A'}
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Status: {getStatusLabel(quotation.status)}</p>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(quotation.status)}`}>
                      {getStatusLabel(quotation.status)}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Date</span>
                      <span className={isDarkMode ? 'text-slate-200' : 'text-gray-800'}>{formatDate(quotation.createdAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Sales Person</span>
                      <span className={`text-right ml-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>{formatSalesmenNames(quotation.salesmen)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Customer</span>
                      <span className={`text-right ml-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                        {getRecipientName(quotation)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Amount</span>
                      <div className="text-right">
                        <div className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>{formatCurrency(quotation.totalAmount)}</div>
                        <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{getShareOfTotal(quotation.totalAmount)}% of filtered value</div>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Aging</span>
                      <span className={isDarkMode ? 'text-slate-200' : 'text-gray-800'}>{getQuotationAgeInDays(quotation.createdAt)} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Priority</span>
                      <span className={`font-medium ${getPriorityTextColor(getPriorityLevel(quotation))}`}>{getPriorityLevel(quotation)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePrintQuotation(quotation)}
                      className="px-3 py-1.5 rounded-md text-sm bg-green-600 text-white hover:bg-green-700 flex items-center"
                      title="Print Quotation"
                    >
                      <FaPrint className="mr-1" /> Print
                    </button>
                    {isCompletable(quotation) && (
                      <button
                        onClick={() => handleCompleteQuotation(quotation)}
                        className="px-3 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 flex items-center"
                        title="Mark as Completed"
                      >
                        <FaCheck className="mr-1" /> Complete
                      </button>
                    )}
                    <button
                      onClick={() => handleViewQuotationDetails(quotation)}
                      className="px-3 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 flex items-center"
                      title="View Reminders"
                    >
                      <FaEye className="mr-1" /> View
                    </button>
                  </div>
                </div>
              )) : (
                <div className={`col-span-full text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  No quotations found for the selected criteria
                </div>
              )}
            </div>
          )
        )}
      </div>

      {/* Quotation Reminders Modal */}
      {showQuotationModal && (
        <div className="fixed inset-0 bg-black/60 overflow-y-auto h-full w-full z-50">
          <div className={`relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                Quotation Reminders - {selectedQuotation?.quotationId || selectedQuotation?.id}
              </h3>
              <div className="flex items-center space-x-2">
                {selectedQuotation && isCompletable(selectedQuotation) && (
                  <button
                    onClick={() => handleCompleteQuotation(selectedQuotation)}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm flex items-center"
                    title="Mark as Completed"
                  >
                    <FaCheck className="mr-1" /> Complete
                  </button>
                )}
                <button
                onClick={() => {
                  setShowQuotationModal(false);
                  setSelectedQuotation(null);
                  setQuotationDetails(null);
                }}
                className={isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'}
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              </div>
            </div>

            {loadingDetails ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className={`mt-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Loading details...</p>
              </div>
            ) : quotationDetails ? (
              <div className="space-y-6">
                {/* Reminders */}
                {quotationDetails.reminders && quotationDetails.reminders.length > 0 ? (
                  <div>
                    <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>Related Reminders</h4>
                    <div className="space-y-3">
                      {quotationDetails.reminders.map((reminder, index) => (
                        <div key={index} className={`border rounded-lg p-3 ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white'}`}>
                          <div className="flex justify-between items-start mb-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reminder.status)}`}>
                              {getStatusLabel(reminder.status)}
                            </span>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{formatDate(reminder.date)}</span>
                          </div>
                          <p className={`text-sm ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>{reminder.message}</p>
                          {reminder.details && (
                            <div className={`mt-2 text-xs ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                              <pre className="whitespace-pre-wrap">{JSON.stringify(reminder.details, null, 2)}</pre>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    No reminders found for this quotation
                  </div>
                )}
              </div>
            ) : (
              <div className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                Failed to load quotation details
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationsReports;
